import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp, Mic, Type, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  MAX_RECORDING_MS,
  MIN_RECORDING_MS,
  useMediaRecorder,
  type StoppedClip,
} from '@/components/voice/useMediaRecorder'
import { usePressTail } from '@/components/voice/usePressTail'

/**
 * VoiceMicButton — the voice-message mic for the Eric thread composer.
 *
 * Gesture model (voice-message-first, locked with Mira 2026-07-04):
 *
 *   - Quick-press: pointerdown → record → release ≥ 750ms → `onClip`
 *     (parent transcribes + auto-sends as a voice message). Release
 *     < 750ms → `onHint('too_short')`, clip discarded.
 *   - Hold-and-lock: drag upward ≥ 60px mid-press → locked (finger can
 *     lift). Locked controls: ✕ discard, "Use as text" → `onClipToText`
 *     (parent transcribes into the textarea — today's dictation, kept),
 *     or the send disc → `onClip` (send as voice).
 *   - Cancel: drag leftward ≥ 60px during the press. No upload.
 *   - Hitting the 25s cap auto-stops and sends (release semantics).
 *
 * While recording, an overlay strip covers the composer's input row
 * (the parent container must be `relative`): pulsing red dot + mono
 * timer + live level bars. Red is the one universal "mic is hot"
 * signal; everything else stays in the quiet palette.
 *
 * The component owns ONLY the gesture + strip. Upload, transcription,
 * and dispatch live in the parent (ThreadChat) which owns auth + thread
 * state. Renders nothing when MediaRecorder is unsupported — the typed
 * composer is the fallback.
 *
 * The press STARTS on the root <button>, but the gesture tail (move /
 * up / cancel) is owned by the shared `usePressTail` hook — window-level
 * capture-phase listeners keyed to the press's pointerId. Element-scoped
 * handlers are not enough: the recording strip mounts OVER the button
 * under the still-held finger, and iOS Safari can drop pointer capture
 * when the DOM changes under a touch — the release then lands on the
 * handler-less strip (or nowhere) and the recorder runs headless to the
 * 25s cap and auto-sends (Mira's iPhone, 2026-07-24 walkthrough; see
 * usePressTail.ts for the delivery contract).
 *
 * pointercancel ≥ 750ms into a real recording transitions to `locked`
 * instead of discarding: WebKit fires pointercancel when it reclassifies
 * a touch (system gesture, notification shade), and silently losing a
 * message the user was mid-way through is worse than handing them the
 * locked panel's explicit send / discard controls.
 */

export type VoiceHintKind = 'too_short' | 'mic_denied' | 'failed'

export type VoiceMicButtonProps = {
  disabled?: boolean
  /** Finished clip to transcribe + send as a voice message. */
  onClip: (clip: StoppedClip) => void
  /** Finished clip to transcribe into the textarea (dictation path).
   *  OPTIONAL — capability-driven: when absent, the locked panel offers
   *  no "Use as text" (the dock hands clips to the thread workspace,
   *  where the composer a transcript would land in doesn't survive the
   *  hop), and a 'text'-routed stop falls back to onClip. */
  onClipToText?: (clip: StoppedClip) => void
  /** Transient user-facing hint (too-short press, mic denied, failure). */
  onHint: (kind: VoiceHintKind) => void
}

type GestureStatus = 'idle' | 'holding' | 'locked'

const LOCK_DRAG_PX = 60
const CANCEL_DRAG_PX = 60
const LEVEL_BARS = 12

export function VoiceMicButton({
  disabled,
  onClip,
  onClipToText,
  onHint,
}: VoiceMicButtonProps) {
  const { t } = useTranslation('voice_messages')
  const [status, setStatus] = useState<GestureStatus>('idle')
  // Mirror of `status` for the window-level gesture handlers — they attach
  // once per press, so reading state through their closure would go stale.
  const statusRef = useRef<GestureStatus>('idle')
  const setGesture = (next: GestureStatus) => {
    statusRef.current = next
    setStatus(next)
  }

  // Press-start position — drives the lock-up / cancel-left thresholds.
  const pressStartRef = useRef<{ x: number; y: number; t: number } | null>(null)
  // Where the finished clip goes: 'send' (quick-press or locked send disc)
  // or 'text' (locked "Use as text"). Read in the recorder's onClip.
  const clipTargetRef = useRef<'send' | 'text'>('send')
  const lockedRef = useRef(false)

  const recorder = useMediaRecorder({
    onClip: (clip) => {
      if (clip.reason === 'permission_revoked') {
        onHint('mic_denied')
        setGesture('idle')
        return
      }
      if (clip.reason === 'user' && clip.durationMs < MIN_RECORDING_MS && !lockedRef.current) {
        onHint('too_short')
        setGesture('idle')
        return
      }
      setGesture('idle')
      lockedRef.current = false
      if (clipTargetRef.current === 'text' && onClipToText) onClipToText(clip)
      else onClip(clip)
      clipTargetRef.current = 'send'
    },
    onError: (err) => {
      const denied =
        err.name === 'NotAllowedError' ||
        err.message?.toLowerCase().includes('permission')
      onHint(denied ? 'mic_denied' : 'failed')
      setGesture('idle')
      lockedRef.current = false
    },
  })

  // Latest-ref mirrors for the press-tail callbacks (the tail attaches once
  // per press): the recorder object and hint callback from the press-start
  // render could be stale by release time.
  const recorderRef = useRef(recorder)
  const onHintRef = useRef(onHint)
  useEffect(() => {
    recorderRef.current = recorder
    onHintRef.current = onHint
  })

  // Window-owned press tail (shared hook — delivery only; the semantics live
  // in movePress/endPress below, referenced via deferred arrows because
  // they're defined past the unsupported early-return).
  const { begin: beginTail, end: endTail } = usePressTail({
    onMove: (ev) => movePress(ev),
    onRelease: () => endPress('release'),
    onCancel: () => endPress('cancel'),
  })

  // The parent disables the mic while a turn is in flight. If that happens
  // MID-press, the root button stops receiving pointer events entirely — the
  // release would be lost and the recording would run headless to the 25s
  // cap. Cancel the recorder proactively (no setState here — the strip
  // visibility derives from `recorder.status` below, and the next pointerdown
  // re-seeds the gesture state; recorder.status is the real is-recording gate).
  // The press tail detaches too, or the eventual orphan release would stop()
  // an already-idle recorder.
  useEffect(() => {
    if (disabled) {
      recorder.cancel()
      lockedRef.current = false
      endTail()
    }
  }, [disabled, recorder, endTail])

  if (recorder.support === 'unsupported') return null

  // Gesture-tail semantics — invoked by the shared press tail with native
  // events. Read recorder/hint/status through refs only (see latest-ref
  // mirrors): the tail invokes them long after this render.

  const movePress = (ev: PointerEvent) => {
    if (statusRef.current !== 'holding') return
    const start = pressStartRef.current
    if (!start) return
    const dy = start.y - ev.clientY // upward = positive
    const dx = start.x - ev.clientX // leftward = positive
    if (dy >= LOCK_DRAG_PX) {
      lockedRef.current = true
      setGesture('locked')
      // Locked = the finger may lift freely; the strip's tap controls take
      // over, so the press tail is done.
      endTail()
      return
    }
    if (dx >= CANCEL_DRAG_PX) {
      recorderRef.current.cancel()
      setGesture('idle')
      pressStartRef.current = null
      endTail()
    }
  }

  // The press ends: 'release' = pointerup (send the clip), 'cancel' =
  // pointercancel (WebKit touch reclassification, system gesture). The hook
  // has already detached the tail + released capture by the time this runs.
  const endPress = (kind: 'release' | 'cancel') => {
    if (statusRef.current !== 'holding') return
    const rec = recorderRef.current
    const elapsed = Date.now() - (pressStartRef.current?.t ?? Date.now())
    pressStartRef.current = null
    // Nothing worth keeping: a sub-750ms press, or a release while the mic is
    // still warming up (getUserMedia pending → 'requesting_permission' — match
    // this EXACT state, not `!== 'recording'`: a 'stopping' recorder, e.g. the
    // 25s cap firing on the same frame, owns a real clip that stop()/onstop
    // will still emit). stop() would degrade to a silent cancel here, so
    // cancel deliberately; a real release gets the "hold to record" hint, an
    // OS-initiated cancel discards quietly.
    if (elapsed < MIN_RECORDING_MS || rec.status === 'requesting_permission') {
      rec.cancel()
      if (kind === 'release') onHintRef.current('too_short')
      setGesture('idle')
      return
    }
    if (kind === 'cancel') {
      // A real recording was interrupted. Hand it to the locked panel —
      // explicit send / discard — rather than silently losing the message.
      lockedRef.current = true
      setGesture('locked')
      return
    }
    clipTargetRef.current = 'send'
    rec.stop()
  }

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    // Gate on the RECORDER's state, not the gesture state — after a
    // disabled-mid-press cancel the gesture state can be stale 'holding',
    // and this fresh press re-seeds it below.
    if (disabled || recorder.status !== 'idle') return
    e.preventDefault()
    pressStartRef.current = { x: e.clientX, y: e.clientY, t: Date.now() }
    lockedRef.current = false
    clipTargetRef.current = 'send'
    setGesture('holding')
    beginTail(e)
    void recorder.start()
  }

  // Strip visibility follows the RECORDER, not the gesture state — the crux of
  // the stuck-mic fix. If a release lands during the getUserMedia warm-up gap,
  // stop()/cancel() settle the recorder to idle WITHOUT emitting a clip, so the
  // gesture state (which only clears via the onClip callback) would leave the
  // strip — and the composer it covers — stuck with no way out. Tying the strip
  // to recorder.status means it vanishes the instant the mic is no longer live,
  // on every path. `status` still drives the holding-vs-locked controls below.
  const isRecording = !disabled && recorder.status !== 'idle' && recorder.status !== 'error'
  const elapsedSec = Math.floor(recorder.durationMs / 1000)
  const remainingSec = Math.max(0, Math.floor(MAX_RECORDING_MS / 1000) - elapsedSec)
  const level = Math.max(0, Math.min(1, recorder.level))

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onPointerDown={onPointerDown}
        onContextMenu={(e) => e.preventDefault()}
        aria-label={t('mic_label')}
        title={t('mic_label')}
        className="inline-flex size-11 shrink-0 touch-none select-none items-center justify-center rounded-full text-muted-foreground transition-colors [-webkit-touch-callout:none] hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
      >
        <Mic className="size-4" />
      </button>

      {/* Recording strip — overlays the composer's input row while the mic is
          hot. Kept in the DOM only while recording; the parent container is
          `relative`. No live region on the root: the ticking timer would be
          announced every second — only the countdown warning is polite.
          Must be as press-inert as the button (select-none + touch-none +
          no context menu + no iOS callout): it mounts UNDER the still-held
          finger, and any selectable text there lets Android Chrome's
          long-press recognizer select a hint word and pop its Touch-to-Search
          panel over the app, killing the gesture (Emil's device, 2026-07-23
          walkthrough); iOS Safari's long-press callout is the same trap. */}
      {isRecording ? (
        <div
          onContextMenu={(e) => e.preventDefault()}
          className="absolute inset-0 z-10 flex touch-none select-none items-center gap-2.5 rounded-2xl bg-background px-3 [-webkit-touch-callout:none]"
        >
          <span
            aria-hidden
            className="size-2.5 shrink-0 animate-pulse rounded-full bg-destructive motion-reduce:animate-none"
          />
          <span className="font-mono text-sm tabular-nums text-foreground">
            {Math.floor(elapsedSec / 60)}:{String(elapsedSec % 60).padStart(2, '0')}
          </span>

          {/* Live level meter — index-weighted so a single peak amplitude reads
              like a waveform. Decorative; the timer is the real signal. */}
          <span aria-hidden className="flex h-5 flex-1 items-center gap-[2px] overflow-hidden">
            {Array.from({ length: LEVEL_BARS }).map((_, i) => {
              const distFromCenter = Math.abs(i - (LEVEL_BARS - 1) / 2) / ((LEVEL_BARS - 1) / 2)
              const weight = 1 - distFromCenter * 0.6
              const heightPct = Math.max(15, level * 100 * weight)
              return (
                <span
                  key={i}
                  className="w-[3px] rounded-sm bg-destructive/70"
                  style={{ height: `${heightPct}%` }}
                />
              )
            })}
          </span>

          {remainingSec <= 5 ? (
            <span
              aria-live="polite"
              className="font-mono text-xs tabular-nums text-muted-foreground"
            >
              {t('seconds_left', { seconds: remainingSec })}
            </span>
          ) : null}

          {status === 'holding' ? (
            // Phone is the primary device — the gesture hint must be visible
            // there, not desktop-only. The level meter flexes down to fit.
            <span className="whitespace-nowrap text-[11px] text-muted-foreground">
              {t('gesture_hint')}
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="ghost"
                size="pill-icon"
                onClick={() => {
                  recorder.cancel()
                  setGesture('idle')
                  lockedRef.current = false
                }}
                aria-label={t('cancel')}
                className="size-11 text-muted-foreground"
              >
                <X className="size-4" />
              </Button>
              {onClipToText ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    clipTargetRef.current = 'text'
                    recorder.stop()
                  }}
                  className="min-h-11 gap-1.5 rounded-full px-3 text-xs font-medium"
                >
                  <Type className="size-3.5" />
                  {t('use_as_text')}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="accent"
                size="pill-icon"
                onClick={() => {
                  clipTargetRef.current = 'send'
                  recorder.stop()
                }}
                aria-label={t('send_now')}
                className="size-11"
              >
                <ArrowUp className="size-4" />
              </Button>
            </span>
          )}
        </div>
      ) : null}
    </>
  )
}
